import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  QueryListItem,
  BlueprintNode,
  DatasetSummary
} from 'src/types/queries'

interface QueryState {
  draft: Partial<QueryListItem>
  original: Partial<QueryListItem> | null
  datasets: DatasetSummary[]
  ui: {
    selectedNodeId: string | null
    isAddingNode: boolean
    addingOperationOver: string | null
  }
}

const initialState: QueryState = {
  draft: {
    title: '',
    public: false,
    blueprint: []
  },
  original: null,
  datasets: [],
  ui: {
    selectedNodeId: null,
    isAddingNode: false,
    addingOperationOver: null
  }
}

const querySlice = createSlice({
  name: 'query',
  initialState,
  reducers: {
    loadQuery(
      state,
      action: PayloadAction<{
        query: QueryListItem
        datasets: DatasetSummary[]
      }>
    ) {
      const { query, datasets } = action.payload
      state.original = query
      state.draft = {
        id: query.id,
        title: query.title,
        public: query.public ?? false,
        blueprint: query.blueprint ? [...query.blueprint] : [],
        description: query.description
      }
      state.datasets = datasets
      state.ui.selectedNodeId = null
      state.ui.isAddingNode = false
      state.ui.addingOperationOver = null
    },

    initNewQuery(state) {
      state.original = null
      state.draft = {
        id: undefined,
        title: '',
        public: false,
        blueprint: []
      }
      state.datasets = []
      state.ui.selectedNodeId = null
      state.ui.isAddingNode = false
      state.ui.addingOperationOver = null
    },

    resetQueryState() {
      return initialState
    },

    updateOriginalToDraft(state) {
      state.original = JSON.parse(JSON.stringify(state.draft))
    },

    updateTitle(state, action: PayloadAction<string>) {
      state.draft.title = action.payload
    },

    togglePublic(state) {
      state.draft.public = !state.draft.public
    },

    addDataset(state, action: PayloadAction<DatasetSummary>) {
      const exists = state.datasets.some((d) => d.id === action.payload.id)
      if (!exists) {
        state.datasets.push(action.payload)
      }
    },

    addNode(state, action: PayloadAction<BlueprintNode>) {
      if (!state.draft.blueprint) state.draft.blueprint = []
      state.draft.blueprint.push(action.payload)
    },

    replaceNode(
      state,
      action: PayloadAction<{ nodeId: string; newNode: BlueprintNode }>
    ) {
      if (state.draft.blueprint) {
        const index = state.draft.blueprint.findIndex(
          (node) => node.nodeId === action.payload.nodeId
        )
        if (index !== -1) {
          state.draft.blueprint[index] = action.payload.newNode
        }
      }
    },

    wrapNode(
      state,
      action: PayloadAction<{ nodeId: string; newNode: BlueprintNode }>
    ) {
      if (state.draft.blueprint) {
        const index = state.draft.blueprint.findIndex(
          (node) => node.nodeId === action.payload.nodeId
        )
        if (index !== -1) {
          state.draft.blueprint[index] = action.payload.newNode
        }
      }
    },

    applyJoinNode(
      state,
      action: PayloadAction<{ leftNodeId: string; joinNode: BlueprintNode }>
    ) {
      if (!state.draft.blueprint) return
      const { leftNodeId, joinNode } = action.payload
      const leftIndex = state.draft.blueprint.findIndex(
        (node) => node.nodeId === leftNodeId
      )
      if (leftIndex !== -1) {
        state.draft.blueprint[leftIndex] = joinNode
        const rightIndex = leftIndex + 1
        if (rightIndex < state.draft.blueprint.length) {
          state.draft.blueprint.splice(rightIndex, 1)
        }
      }
      state.ui.addingOperationOver = null
      state.ui.selectedNodeId = joinNode.nodeId
    },

    removeNode(state, action: PayloadAction<string>) {
      if (!state.draft.blueprint) return
      const index = state.draft.blueprint.findIndex(
        (node) => node.nodeId === action.payload
      )
      if (index === -1) return
      const node = state.draft.blueprint[index]
      if (node.type === 'dataset') {
        state.draft.blueprint.splice(index, 1)
      } else if (node.type === 'join') {
        state.draft.blueprint[index] = node.left
        state.draft.blueprint.splice(index + 1, 0, node.right)
      } else {
        state.draft.blueprint[index] = node.source
      }
    },

    selectNode(state, action: PayloadAction<string>) {
      state.ui.selectedNodeId = action.payload
      state.ui.isAddingNode = false
      state.ui.addingOperationOver = null
    },

    startAddingNode(state) {
      state.ui.isAddingNode = true
      state.ui.selectedNodeId = null
      state.ui.addingOperationOver = null
    },

    startAddingOperation(state, action: PayloadAction<string>) {
      state.ui.addingOperationOver = action.payload
      state.ui.selectedNodeId = null
      state.ui.isAddingNode = false
    },

    clearSelection(state) {
      state.ui.selectedNodeId = null
      state.ui.isAddingNode = false
      state.ui.addingOperationOver = null
    }
  }
})

export const {
  loadQuery,
  initNewQuery,
  resetQueryState,
  updateOriginalToDraft,
  updateTitle,
  togglePublic,
  addDataset,
  addNode,
  replaceNode,
  wrapNode,
  applyJoinNode,
  removeNode,
  selectNode,
  startAddingNode,
  startAddingOperation,
  clearSelection
} = querySlice.actions

export default querySlice.reducer
